import type { FirestoreError } from 'firebase/firestore'
import { toast } from 'sonner'
import { useAuthStore } from '@/store/auth.store'
import { reportClientError } from './errorReporting'

// Error callback for every `onSnapshot` in the app.
//
// Until this existed, most subscriptions passed no error callback at all, so a
// missing composite index or a rules change produced an empty list and nothing
// else: no console entry, no toast, no server-side log, and a spinner that
// never resolved. "A revisão que agendei não aparece na Manutenção" is exactly
// what that looks like from the outside — indistinguishable from "there is no
// data". Failing loudly is the whole point of this helper.

const CODE_MESSAGES: Partial<Record<FirestoreError['code'], string>> = {
  'permission-denied': 'Sem permissão para ver estes dados.',
  'failed-precondition': 'Consulta sem índice configurado — contacte o suporte.',
  'resource-exhausted': 'Limite de leituras atingido. Tente novamente mais tarde.',
  'unauthenticated': 'Sessão expirada — inicie sessão novamente.',
}

// Transient by nature: the Firestore SDK retries on its own and the listener
// recovers without anyone doing anything. Surfacing these would mean a toast on
// every network blip and on every sign-out teardown, which trains people to
// ignore the toasts that do matter.
const SILENT_CODES = new Set<FirestoreError['code']>(['unavailable', 'cancelled', 'aborted', 'deadline-exceeded'])

/**
 * Builds the error callback for an `onSnapshot` subscription.
 *
 * @param label Human-readable name of what failed to load, used both in the
 *   logs and in the toast ("Erro ao carregar <label>").
 * @param onSettled Called on every error, including silenced ones — use it to
 *   clear the hook's `isLoading`, so a failed subscription resolves its
 *   spinner instead of hanging forever.
 */
export function subscriptionError(
  label: string,
  onSettled?: () => void,
): (err: FirestoreError) => void {
  return (err) => {
    onSettled?.()

    console.error(`[firestore] ${label}: ${err.code}`, err.message)
    if (SILENT_CODES.has(err.code)) return

    // A listener that outlives the session reports permission-denied on the
    // way out. That is teardown noise, not a real failure.
    if (!useAuthStore.getState().session) return

    reportClientError({
      message: `[firestore] ${label}: ${err.code} — ${err.message}`,
      stack: err.stack,
      source: 'firestore-listener',
    })

    // One toast per subscription: a listener can fire its error callback
    // repeatedly while retrying, and sonner replaces rather than stacks when
    // the id matches.
    toast.error(
      CODE_MESSAGES[err.code] ?? `Erro ao carregar ${label}.`,
      { id: `firestore-${label}`, description: CODE_MESSAGES[err.code] ? `Erro ao carregar ${label}.` : undefined },
    )
  }
}

// A `limit()` window that comes back exactly full is the only observable sign
// that it has started dropping data — Firestore does not say "there was more".
// Every capped listener in the app used to truncate in complete silence, which
// is the shape of "agendei uma revisão e não aparece": indistinguishable from
// the record not existing.
//
// Reported once per label per session: a full window stays full, and the
// listener re-delivers on every write.
const truncationReported = new Set<string>()

export function warnIfTruncated(label: string, size: number, windowSize: number): void {
  if (size < windowSize) return

  console.warn(
    `[firestore] ${label}: janela de ${windowSize} registos cheia — os mais antigos não estão a ser carregados`,
  )

  if (truncationReported.has(label)) return
  truncationReported.add(label)

  reportClientError({
    message: `[firestore] ${label}: janela de ${windowSize} registos cheia`,
    source: 'firestore-listener',
  })
}
